import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  compactBotsListForPersist,
  fetchBotsListForRegistry,
  patchBotConfigUrlInMemory,
  setBotConfigUrlInBotsList,
} from '../../../config/botsListRegistry';
import { saveBotConfig, saveBotsList } from '../../../services/admin/botAdminApi';
import classes from './SaveJsonToBucketButton.module.css';

async function saveBotConfigAndRegistry({ domainName, botName, content, urlPath }) {
  const res = await saveBotConfig({ domainName, botName, content });
  if (!res?.ok) return res;
  const source = await fetchBotsListForRegistry();
  const { botsList } = setBotConfigUrlInBotsList(source, domainName, botName, { urlPath });
  const listRes = await saveBotsList({
    directory: 'bots',
    fileName: 'botsList.json',
    content: compactBotsListForPersist(botsList),
  });
  if (!listRes?.ok) return listRes;
  patchBotConfigUrlInMemory(domainName, botName, { urlPath });
  return listRes;
}

export default function SaveJsonToBucketButton({
  domainName,
  botName,
  content,
  getContent,
  urlPath,
  saveKind = 'botConfig',
  directory,
  fileName,
  buttonLabel,
  disabled,
  confirmTitleId = 'save-json-confirm-title',
}) {
  const { texts, direction } = useSelector((state) => state.translate);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const resolvedLabel = buttonLabel ?? texts.saveJsonToBucket;

  const onConfirm = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      if (!window.config?.baseApiUrl) {
        setError(texts.saveJsonFailed);
        return;
      }
      const payload = typeof getContent === 'function' ? await getContent() : content;
      if (payload == null || (Array.isArray(payload) && payload.length === 0)) {
        setError(texts.saveJsonFailed);
        return;
      }
      const res = saveKind === 'botsList'
        ? await saveBotsList({ directory, fileName, content: payload })
        : await saveBotConfigAndRegistry({
            domainName,
            botName,
            content: payload,
            urlPath,
          });
      if (!res?.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || texts.saveJsonFailed);
      }
      setSuccess(true);
      setShowConfirm(false);
    } catch {
      setError(texts.saveJsonFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.Wrap}>
      <button
        type="button"
        className={classes.BtnPrimary}
        onClick={() => { setShowConfirm(true); setSuccess(false); setError(''); }}
        disabled={saving || disabled}
      >
        {saving ? texts.savingJsonToBucket : resolvedLabel}
      </button>
      {error ? <p className={classes.Error} role="alert">{error}</p> : null}
      {success ? <p className={classes.Success}>{texts.saveJsonSuccess}</p> : null}
      {showConfirm ?
        <div className={classes.Backdrop} onClick={() => !saving && setShowConfirm(false)} role="presentation">
          <div
            className={classes.Dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby={confirmTitleId}
            dir={direction || 'rtl'}
            onClick={(e) => e.stopPropagation()}
          >
            <p id={confirmTitleId} className={classes.Title}>
              {texts.saveJsonOverwriteTitle}
            </p>
            <p className={classes.Message}>
              {texts.saveJsonOverwriteConfirm}
            </p>
            <div className={classes.Actions}>
              <button
                type="button"
                className={classes.Btn}
                onClick={() => setShowConfirm(false)}
                disabled={saving}
              >
                {texts.saveJsonCancel}
              </button>
              <button
                type="button"
                className={classes.BtnPrimary}
                onClick={onConfirm}
                disabled={saving}
              >
                {saving ? texts.savingJsonToBucket : texts.saveJsonConfirm}
              </button>
            </div>
          </div>
        </div>
        : null}
    </div>
  );
}
